// /agents — the project's agents, in the old page's layout: a collapsible left rail of agent rows in
// three sections (My agents, Shared with me, Organization) with hover actions and a ⋯ menu, a rail
// footer "Build an agent", and a main pane explaining agents. A row opens the agent's page; "Ask"
// opens it with its chat.
import {
  Bot,
  ChevronDown,
  ChevronRight,
  GitFork,
  Package,
  LayoutGrid,
  Loader2,
  PanelLeftClose,
  PanelLeftOpen,
  RefreshCw,
  Plus,
  Sparkles,
} from 'lucide-react';
import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAgentsHost, useAgentsKey, useAgentsQuery as useApiQuery } from './host.tsx';
import { AgentIcon } from './AgentIcon.tsx';
import { AgentPage } from './AgentPage.tsx';
import { CreateAgentDialog } from './dialogs.tsx';
import { AllAgentsPanel } from './AllAgents.tsx';
import { EvaluationsPanel } from './Evaluations.tsx';
import { changesText, SourceBadge, UsagePanel, useAgentCatalog, type CatalogAgent } from './catalog.tsx';
import { AgentTabs } from './tabs.tsx';
import { groupAgents, type AgentSummary } from './model.ts';
import { btnGhost, btnPrimary, iconBtn, input, Modal, unwrap } from './ui.tsx';

const A = '/agents' as const;
const RAIL_KEY = 'agentsRailOpen';

/** The Agents page (the host's `/agents`): the rail of agents and the empty main. */
export function AgentsPage({
  agentKey,
}: {
  /** A project agent open beside the rail (`/agents/<key>`): its page, the rail kept and the agent highlighted. */
  agentKey?: string;
}) {
  const host = useAgentsHost();
  const api = host.client.fetch;
  const k = useAgentsKey();
  const navigate = useNavigate();
  // ?view=evals: the main pane shows the Evaluations of every agent (ADR 0035).
  const [search, setSearch] = useSearchParams();
  const evalsOpen = !agentKey && !!host.evals && search.get('view') === 'evals';
  // ADR 0038: ?persona=<key> opens a platform persona (read-only); ?view=usage the usage index (?app=<key>: one app's).
  const personaOpen = agentKey ? null : search.get('persona');
  const usageOpen = !agentKey && search.get('view') === 'usage';
  // ?view=all: every project agent in one table (who uses it, its tests); Where used and Evaluations open from it.
  const allOpen = !agentKey && search.get('view') === 'all';
  const usageApp = search.get('app');
  // The platform's agents are folded until opened (or one of them is open).
  const [platformOpen, setPlatformOpen] = React.useState(false);
  // "New agent": its menu (describe it to the builder, or an empty agent) and the builder's dialog.
  const [newMenu, setNewMenu] = React.useState(false);
  const [building, setBuilding] = React.useState(false);
  const platformShown = platformOpen || !!personaOpen;
  // On a project agent's own URL, a rail view (a persona, the evaluations, where used) opens on the Agents URL.
  const setView = (patch: Record<string, string | null>) => {
    if (agentKey) {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(patch)) if (v != null) q.set(k, v);
      const qs = q.toString();
      return navigate(`${host.paths.agents()}${qs ? `?${qs}` : ''}`);
    }
    return setSearch(
      (p) => {
        const n = new URLSearchParams(p);
        for (const [k, v] of Object.entries(patch))
          if (v == null) n.delete(k);
          else n.set(k, v);
        return n;
      },
      { replace: true },
    );
  };
  const catalog = useAgentCatalog();
  const docs = catalog.data?.agents ?? [];
  const platform = docs.filter((d) => d.kind === 'persona');
  const openSubject = (subject: string) => {
    const [kind, key] = [subject.slice(0, subject.indexOf(':')), subject.slice(subject.indexOf(':') + 1)];
    if (kind === 'persona') setView({ persona: key, view: null, app: null, tab: null });
    else navigate(host.paths.agent(key, 'tab=used-in'));
  };
  const showEvals = (on: boolean) =>
    agentKey
      ? setView({ view: on ? 'evals' : null })
      : setSearch(
          (p) => {
            const n = new URLSearchParams(p);
            if (on) n.set('view', 'evals');
            else n.delete('view');
            return n;
          },
          { replace: true },
        );
  // The list lives in the shared query cache: a save on an agent's page, or a chat tool that changed
  // the app's agents, marks it stale and it reloads in place.
  const listed = useApiQuery(k(), () => unwrap(api.GET(A, {})));
  const agents: AgentSummary[] | null = listed.data?.agents ?? null;
  const canCreate = listed.data?.canCreate ?? false;
  const loading = listed.loading;
  const [actionErr, setErr] = React.useState('');
  const err = actionErr || listed.error || '';
  const [creating, setCreating] = React.useState(false);
  const [railOpen, setRailOpen] = React.useState(() => {
    try {
      return localStorage.getItem(RAIL_KEY) !== '0';
    } catch {
      return true;
    }
  });

  const toggleRail = () =>
    setRailOpen((o) => {
      try {
        localStorage.setItem(RAIL_KEY, o ? '0' : '1');
      } catch {
        /* storage off */
      }
      return !o;
    });

  const { reload } = listed;

  const open = (key: string, chat = false) => navigate(host.paths.agent(key, chat ? 'chat=1' : undefined));

  const Row = (a: AgentSummary) => {
    const visTitle =
      a.visibility === 'organization'
        ? 'Visible to the organization'
        : a.sharedCount
          ? `Shared with ${a.sharedCount}`
          : 'Private';
    return (
      <div key={a.key} className="group relative" data-testid={`agent-${a.key}`}>
        <button
          type="button"
          data-testid={`agent-open-${a.key}`}
          onClick={() => open(a.key)}
          aria-current={a.key === agentKey ? 'page' : undefined}
          data-selected={a.key === agentKey ? 'true' : undefined}
          title={a.description ?? a.title}
          className={
            'w-full text-left rounded-md px-2 py-1.5 flex gap-2 items-start border-0 cursor-pointer hover:bg-card-border/30 ' +
            (a.key === agentKey ? 'bg-nav-active/10' : 'bg-transparent')
          }
        >
          <AgentIcon icon={a.icon} size={18} className="mt-0.5 shrink-0" />
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-page-text truncate">{a.title}</span>
            {a.description ? (
              <span className="block text-xs text-page-muted truncate">{a.description}</span>
            ) : null}
          </span>
          <span className="sr-only" data-testid={`agent-visibility-${a.key}`} data-visibility={a.visibility}>
            {visTitle}
          </span>
        </button>
      </div>
    );
  };

  const groups = groupAgents(agents ?? []);

  return (
    <div className="flex h-full min-h-0" data-tour="page-agents" data-testid="agents-page">
      {!railOpen ? (
        <div className="w-10 shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col items-center py-3 gap-2">
          <button
            type="button"
            data-testid="agents-rail-open"
            onClick={toggleRail}
            title="Show agents list"
            className={iconBtn}
          >
            <PanelLeftOpen size={18} />
          </button>
          <Bot size={16} className="text-nav-active mt-1" />
        </div>
      ) : null}
      <aside
        className={
          (railOpen ? 'w-72' : 'hidden') +
          ' shrink-0 border-r border-card-border bg-card-bg/40 flex flex-col min-h-0'
        }
      >
        <div className="px-4 py-4 flex items-center gap-2 border-b border-card-border">
          <Bot size={18} className="text-nav-active" />
          <span className="font-semibold text-page-text">Agents</span>
          <button
            type="button"
            data-testid="agents-refresh"
            onClick={() => (setErr(''), reload())}
            title="Refresh"
            className={iconBtn + ' ml-auto'}
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          </button>
          <button
            type="button"
            data-testid="agents-rail-close"
            onClick={toggleRail}
            title="Collapse agents list"
            className={iconBtn}
          >
            <PanelLeftClose size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-2">
          {loading && !agents ? (
            <div className="flex items-center gap-2 text-sm text-page-muted px-2 py-3">
              <Loader2 size={16} className="animate-spin" /> Loading agents…
            </div>
          ) : null}
          {agents && !agents.length ? (
            <div className="text-sm text-page-muted px-2 py-3" data-testid="agents-empty">
              No agents yet in this project.{canCreate ? ' Build one below.' : ''}
            </div>
          ) : null}
          {groups.map((g) => (
            <div key={g.id} className="mb-2" data-testid={`agents-section-${g.id}`}>
              <div className="px-2 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-page-muted">
                {g.label} <span className="font-normal">{g.items.length}</span>
              </div>
              {g.items.map(Row)}
            </div>
          ))}
          {platform.length ? (
            <div className="mb-2" data-testid="agents-section-platform">
              <button
                type="button"
                data-testid="agents-platform-toggle"
                onClick={() => setPlatformOpen(!platformShown)}
                className="w-full flex items-center gap-1 px-2 pt-2 pb-1 border-0 bg-transparent cursor-pointer text-[11px] font-semibold uppercase tracking-wide text-page-muted"
              >
                {platformShown ? <ChevronDown size={12} /> : <ChevronRight size={12} />} Platform{' '}
                <span className="font-normal">{platform.length}</span>
              </button>
              {platformShown
                ? platform.map((d) => (
                    <button
                      key={d.key}
                      type="button"
                      data-testid={`platform-agent-${d.key}`}
                      onClick={() => setView({ persona: d.key, view: null, app: null, tab: null })}
                      title={d.description ?? d.title}
                      className={
                        'w-full text-left rounded-md px-2 py-1.5 flex gap-2 items-start border-0 cursor-pointer hover:bg-card-border/30 ' +
                        (personaOpen === d.key ? 'bg-nav-active/10' : 'bg-transparent')
                      }
                    >
                      <Package size={16} className="mt-0.5 shrink-0 text-page-muted" />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-page-text truncate">{d.title}</span>
                        {d.description ? (
                          <span className="block text-xs text-page-muted truncate">{d.description}</span>
                        ) : null}
                      </span>
                    </button>
                  ))
                : null}
            </div>
          ) : null}
        </div>
        <div className="p-2 border-t border-card-border grid gap-2">
          {canCreate || host.BuildAgentRequest ? (
            <div className="relative">
              <button
                type="button"
                data-testid="agents-new"
                onClick={() =>
                  host.BuildAgentRequest && canCreate
                    ? setNewMenu(!newMenu)
                    : host.BuildAgentRequest
                      ? setBuilding(true)
                      : setCreating(true)
                }
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-md text-sm px-3 py-2 border border-dashed border-card-border bg-transparent text-page-muted hover:text-page-text hover:bg-card-border/30 cursor-pointer"
              >
                <Plus size={14} /> New agent
                {host.BuildAgentRequest && canCreate ? <ChevronDown size={12} /> : null}
              </button>
              {newMenu ? (
                <div
                  className="absolute bottom-full left-0 right-0 z-30 mb-1 rounded-md border border-card-border bg-card-bg p-1 shadow-lg"
                  data-testid="agents-new-menu"
                >
                  <button
                    type="button"
                    data-testid="agents-new-describe"
                    className="w-full text-left rounded px-2.5 py-2 border-0 bg-transparent cursor-pointer hover:bg-card-border/30"
                    onClick={() => (setNewMenu(false), setBuilding(true))}
                  >
                    <span className="flex items-center gap-1.5 text-sm text-page-text">
                      <Sparkles size={14} /> Describe it
                    </span>
                    <span className="block text-xs text-page-muted">
                      The builder makes it: instructions, tools, tests
                    </span>
                  </button>
                  <button
                    type="button"
                    data-testid="agents-new-empty"
                    className="w-full text-left rounded px-2.5 py-2 border-0 bg-transparent cursor-pointer hover:bg-card-border/30"
                    onClick={() => (setNewMenu(false), setCreating(true))}
                  >
                    <span className="flex items-center gap-1.5 text-sm text-page-text">
                      <Plus size={14} /> Empty agent
                    </span>
                    <span className="block text-xs text-page-muted">
                      A name; you add the rest on its tabs
                    </span>
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
          <button
            type="button"
            data-testid="agents-all"
            onClick={() =>
              setView(allOpen ? { view: null, app: null } : { view: 'all', persona: null, app: null })
            }
            className={
              'w-full inline-flex items-center justify-center gap-1.5 rounded-md text-sm px-3 py-2 border border-card-border cursor-pointer ' +
              (allOpen || evalsOpen || usageOpen
                ? 'bg-nav-active/10 text-page-text'
                : 'bg-transparent text-page-muted hover:text-page-text hover:bg-card-border/30')
            }
          >
            <LayoutGrid size={14} /> All agents
          </button>
          {host.BuildAgentRequest ? null : (host.buildRequest ?? null)}
        </div>
      </aside>

      <main className="flex-1 min-w-0 min-h-0 relative bg-page-bg overflow-y-auto">
        {err ? (
          <div className="mx-6 mt-4 text-sm text-red-600" data-testid="agents-error">
            {err}
          </div>
        ) : null}
        {allOpen || (!evalsOpen && !usageOpen && !personaOpen && !agentKey && !!agents?.length) ? (
          <AllAgentsPanel
            agents={agents ?? []}
            openUsage={() => setView({ view: 'usage', app: null })}
            openEvals={host.evals ? () => showEvals(true) : null}
          />
        ) : null}
        {evalsOpen ? <EvaluationsPanel /> : null}
        {!evalsOpen && usageOpen ? (
          <UsagePanel app={usageApp} openAgent={openSubject} openApp={(app) => setView({ app })} />
        ) : null}
        {agentKey ? <AgentPage agentKey={agentKey} embedded /> : null}
        {!allOpen && !evalsOpen && !usageOpen && personaOpen ? (
          <PlatformAgentView
            agent={platform.find((d) => d.key === personaOpen) ?? null}
            loading={catalog.loading}
            tab={search.get('tab') ?? 'instructions'}
            setTab={(tab) => setView({ tab })}
          />
        ) : null}
        <div
          className={
            (allOpen || evalsOpen || usageOpen || personaOpen || agentKey || agents?.length
              ? 'hidden '
              : '') +
            'absolute inset-0 flex flex-col items-center justify-center text-center px-6 pointer-events-none'
          }
        >
          <Bot size={40} className="text-page-muted mb-3" />
          <div className="text-page-text font-medium">Your project agents</div>
          <div className="text-sm text-page-muted mt-1 max-w-md">
            Pick an agent from the left to open its page: its persona, the operations it may call, its
            safeguards and the runs it made, with a chat to try it. Each agent works over the project through
            its tools.
          </div>
          {canCreate ? (
            <button
              type="button"
              data-testid="agents-new-main"
              onClick={() => setCreating(true)}
              className={btnGhost + ' mt-4 pointer-events-auto'}
            >
              <Plus size={14} /> New agent
            </button>
          ) : null}
        </div>
      </main>

      {creating ? (
        <CreateAgentDialog
          taken={new Set((agents ?? []).map((a) => a.key))}
          onClose={() => setCreating(false)}
          onCreated={(a) => {
            setCreating(false);
            navigate(host.paths.agent(a.key, 'edit=1'));
          }}
        />
      ) : null}
      {building && host.BuildAgentRequest ? (
        <host.BuildAgentRequest onClose={() => setBuilding(false)} />
      ) : null}
    </div>
  );
}

/**
 * A platform persona (ADR 0038): its manifest as the SDK ships it, read-only, in the agent tabs — with the same header
 * as a project agent's: its name and key, what it does, one line of facts (where it comes from, its model, what it may
 * do, whether this project changed it) and, on the right, how to change it for this project.
 */
/**
 * Make a project agent from a platform agent: its name and what it should do in this project (required — the builder
 * picks its tools from it); the host files the request (e.g. a card on a board, which then opens).
 */
function ForkDialog({ agent, onClose }: { agent: CatalogAgent; onClose(): void }) {
  const host = useAgentsHost();
  const navigate = useNavigate();
  const [icon, setIcon] = React.useState(agent.icon ?? '🤖');
  const [name, setName] = React.useState(copyName(host.session, agent.title));
  const [need, setNeed] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const go = async () => {
    if (!host.requestAgent || !name.trim() || !need.trim()) return;
    setBusy(true);
    setErr('');
    try {
      const r = await host.requestAgent({
        title: `${icon.trim() ? `${icon.trim()} ` : ''}${name.trim()}`,
        details: [
          `A project agent made from the platform agent “${agent.title}” (${agent.key}): copy its instructions,`,
          'guardrails and harness; give it this project’s operations for what its chat tools do, and add to an app',
          'what is missing.',
          '',
          `What it should do in this project: ${need.trim()}`,
        ].join('\n'),
      });
      onClose();
      navigate(r.path);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };
  return (
    <Modal
      title={
        <>
          <GitFork size={16} /> Make a project agent from “{agent.title}”
        </>
      }
      onClose={onClose}
      testId="platform-agent-fork-dialog"
      wide="lg"
      footer={
        <>
          <button type="button" className={btnGhost} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={btnPrimary}
            data-testid="platform-agent-fork-go"
            disabled={busy || !name.trim() || !need.trim()}
            title={need.trim() ? undefined : 'Say what it should do first'}
            onClick={() => void go()}
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Make it with the
            builder
          </button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="m-0 text-xs text-page-muted">
          The builder makes a copy that belongs to this project — you can change it, and it ships with the
          app. The platform’s {agent.title} stays as it is.
        </p>
        <label className="block">
          <span className="text-xs text-page-muted">Name</span>
          <span className="flex gap-2">
            <input
              className={input + ' !w-16 shrink-0 text-center'}
              value={icon}
              maxLength={16}
              onChange={(e) => setIcon(e.target.value)}
            />
            <input
              className={input + ' flex-1 min-w-0'}
              data-testid="platform-agent-fork-name"
              value={name}
              maxLength={120}
              onChange={(e) => setName(e.target.value)}
            />
          </span>
        </label>
        <label className="block">
          <span className="text-xs text-page-muted">What should it do in this project?</span>
          <textarea
            className={input + ' min-h-[88px]'}
            data-testid="platform-agent-fork-need"
            value={need}
            maxLength={4000}
            placeholder="e.g. Answer only about this project’s data; never change data."
            onChange={(e) => setNeed(e.target.value)}
          />
          <span className="block mt-1 text-xs text-page-muted">
            The builder picks its tools from this — which apps and data it works over, what it must not do.
          </span>
        </label>
        {err ? <div className="text-sm text-red-600">{err}</div> : null}
      </div>
    </Modal>
  );
}

/** A project agent's name made from a platform agent: the project's and its — “Demo portal data analyst”. */
export function copyName(session: readonly string[] | undefined, title: string): string {
  const project = (session ?? []).find((l) => l.startsWith('Project: '))?.slice('Project: '.length) ?? '';
  const words = project.replace(/[_-]+/g, ' ').trim();
  if (!words) return `${title} (project)`;
  return `${words[0]!.toUpperCase()}${words.slice(1)} ${title[0]!.toLowerCase()}${title.slice(1)}`;
}

function PlatformAgentView({
  agent,
  loading,
  tab,
  setTab,
}: {
  agent: CatalogAgent | null;
  loading: boolean;
  tab: string;
  setTab(id: string): void;
}) {
  const host = useAgentsHost();
  const [forking, setForking] = React.useState(false);
  const models = useApiQuery([...host.cache.agents, 'agent-models'], () => host.models!.resolve(), {
    enabled: !!host.models,
  });
  if (!agent)
    return (
      <div className="p-6 text-sm text-page-muted">
        {loading ? <Loader2 size={14} className="animate-spin" /> : 'No such platform agent on this host.'}
      </div>
    );
  const role = agent.model.role ?? 'standard';
  const model = models.data?.roles.find((r) => r.role === role)?.model ?? null;
  const dot = <span aria-hidden>·</span>;
  const changes = changesText(agent, host.toolChanges);
  return (
    <div className="px-6 py-4 max-w-[1400px]" data-testid="platform-agent-page" data-agent-name={agent.key}>
      <header className="flex flex-wrap items-start gap-4 mt-3 mb-5">
        <Package size={34} className="mt-1 shrink-0 text-page-muted" />
        <div className="flex-1 min-w-[min(100%,420px)]">
          <h1
            className="text-2xl font-semibold text-page-title m-0 flex flex-wrap items-baseline gap-x-2"
            data-testid="agent-title"
          >
            {agent.title}
            <span className="font-mono text-xs font-normal text-page-muted">{agent.key}</span>
          </h1>
          {agent.description ? (
            <p className="text-page-muted mt-1 mb-0 max-w-3xl">{agent.description}</p>
          ) : null}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 text-xs text-page-muted">
            <SourceBadge source={agent.source} override={agent.override} testId="agent-source" />
            {dot}
            <span
              title={
                agent.model.role ? 'Its own kind of model' : 'The model picked in the chat; Standard if none'
              }
            >
              {model ? (
                <span className="font-mono">{model}</span>
              ) : (
                `Model: ${agent.model.role ?? 'the chat’s'}`
              )}
              {agent.model.role ? '' : ' (the chat’s)'}
            </span>
            {agent.access ? (
              <>
                {dot}
                <span data-testid="platform-agent-changes" title={changes.all}>
                  {changes.text}
                  {agent.key === 'orchestrator' && agent.access === 'write'
                    ? '; hands the rest to other agents'
                    : ''}
                </span>
              </>
            ) : null}
            <span data-testid="platform-agent-chat">· Used by {host.platformChat ?? 'the host’s chat'}</span>
            {agent.override ? (
              <span data-testid="platform-agent-override">· changed here the old way (Project override)</span>
            ) : null}
          </div>
          <p className="mt-2 mb-0 max-w-3xl text-xs text-page-muted" data-testid="platform-agent-note">
            A platform agent is the same in every project: it works in{' '}
            {host.platformChat ?? 'the host’s chat'}, and it is not deployed with an app. For an app of this
            project, make a project agent from it — a copy that belongs to the project, that you change and
            that ships with the app.
          </p>
        </div>
        {host.requestAgent || host.BuildAgentRequest ? (
          <div className="flex flex-wrap items-center justify-end gap-2 ml-auto">
            <button
              type="button"
              className={btnPrimary}
              data-testid="platform-agent-fork"
              onClick={() => setForking(true)}
            >
              <GitFork size={14} /> Make a project agent from it
            </button>
          </div>
        ) : null}
      </header>
      {forking && host.requestAgent ? (
        <ForkDialog agent={agent} onClose={() => setForking(false)} />
      ) : forking && host.BuildAgentRequest ? (
        <host.BuildAgentRequest
          heading={`Make a project agent from “${agent.title}”`}
          title={copyName(host.session, agent.title)}
          details={[
            `From the platform agent “${agent.title}” (${agent.key}): copy its instructions, guardrails and harness;`,
            'give it this project’s operations for what its chat tools do, and add to an app what is missing.',
            '',
            'What it should do in this project: ',
          ].join('\n')}
          onClose={() => setForking(false)}
        />
      ) : null}
      <AgentTabs ctx={{ agent }} tab={tab} setTab={setTab} />
    </div>
  );
}
